---
title: Services on this computer
lede: Make a database, broker or cache running on your computer reachable from one workspace's sessions.
order: 40
part: Shape the machine
---

Services on this computer let a session reach a service already running in Docker on your computer, such as a database, a message broker or a cache. Set them in **Workspace settings › Services**.

:::warn This opens the sandbox on purpose
The VM boundary does not protect a service you list here. An agent steered by a repository or a dependency can read everything it holds and destroy it. Point it at a disposable or anonymised development instance, with least-privilege credentials.
:::

![Services on this computer](media/ws-services.webp)

### Adding a service

Airlock asks Docker what is running; **Refresh** asks again. Each container is a row with a switch, its kind and its ports.

1. Switch a container on.
2. **Name in your code**: the variable your repositories read, such as `DATABASE_URL`. Names your repositories already use are offered.
3. **Address**: the URL you use on this computer, such as `postgresql://localhost:5432/app`. It is prefilled from the container's environment, with the credentials it started with; check it, because a container that declared none gets placeholders.

Services you turned on stay at the top of the page.

### What a session gets

Each entry opens exactly one address on one port, never your whole computer or your network. **Your code receives** shows the variables every repository gets:

- the variable itself, holding the URL (`localhost` is rewritten to your computer's address as the sandbox sees it);
- `NAME_HOST` and `NAME_PORT`.

A repository that sets the same name itself keeps its own. A project variable can be written from any of the three, such as `${NAME_HOST}:${NAME_PORT}`.

### Per workspace only

There is no application-wide list. Each workspace decides for its own repositories, and other workspaces' sessions are unaffected.

### Containers a workspace declares

A workspace may declare the containers it needs on your computer, from its settings in the account. They are listed first on this page, under **Containers this workspace needs**, with your container runner and each one's state. Docker Desktop, OrbStack, Colima, Rancher Desktop, Podman and Docker Engine all serve; with none, the page offers links to get one.

Press **Start**, or **Recreate** on one whose declaration changed. Named volumes keep their data. Nothing starts by itself, and **Check again** asks the runner again.
